import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Body } from "@vds/typography";
import { DropdownSelect } from "@vds/selects";
import NumberInput from "../../NumberInput";
import { FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { Button } from "@vds/buttons";
import { Icon } from "@vds/icons";
import { Line } from "@vds/lines";
import { useSelector, useDispatch } from "react-redux";
import {
  setAdvancedConfigAppearances,
  setAdvancedConfigThresholds,
  setAdvancedConfigRadio,
  clearAdvancedConfigAppearances
} from "../../../../store/actions";
import { ADVANCED_CONFIG, PLACEHOLDER_IMAGE, APPEARANCE_TEXT, CREATE_APPEARANCE, APPEARANCE_RUNTIME_STATUS, ASSOCIATE_STREAMS } from "../../../../constants/enrolledAppearanceConstants";
import { ImageGalleryContainer, ThumbnailImage, NoAppearancesMessage } from "./styles";
import "./EnhancedToggleField.css";
import { fetchAppearancesRequest, downloadAppearanceImagesRequest } from "../../../../store/actions/ea-actions";
import { Notification } from "@vds/notifications";
import DeployModal from "../../../../pages/EnrolledAppearance/components/DeployModal";
import ErrorModal from "../../../../pages/EnrolledAppearance/components/ErrorModal";
import ProcessingRequiredModal from "./ProcessingRequiredModal";
import { Loader } from "@vds/loaders";
import { getFullCVAppName, getSessionStorageData, getStandardizedRole } from "../../../utility/Utils";
import { CVAPP_PEOPLE_COUNT_OA } from "../../../constants/cvappConstants";
import { STORAGE } from "../../../constants/storageConstants";
import { ROUTES } from "../../../constants/routesConstants";
import "../../../../pages/EnrolledAppearance/common-styles.css";

const buildEnrollInfoMessage = (siteName, cameraName) => (
  <span>
    {ADVANCED_CONFIG.DEPLOY_ENROLL_SITE_MESSAGE_PREFIX}
    <strong>{siteName}</strong>
    {ADVANCED_CONFIG.DEPLOY_ENROLL_SITE_MESSAGE_MID}
    <strong>{cameraName}</strong>
    {ADVANCED_CONFIG.DEPLOY_ENROLL_SITE_MESSAGE_SUFFIX}
  </span>
);

export const EnhancedToggleField = ({
  keyName,
  setPayload,
  payload,
  parameterKeyName,
  style,
  setIsTampered,
  setErrorInFormInput,
  cvappid,
  action,
  siteId
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  //frequently used constant keys
  const CAPTURE_THRESHOLD_KEY = ADVANCED_CONFIG.APPEARANCE_CAPTURE_THRESHOLD;
  const MATCHING_THRESHOLD_KEY = ADVANCED_CONFIG.APPEARANCE_MATCHING_THRESHOLD;

  //const placeholderImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODEiIGhlaWdodD0iODEiIHZpZXdCb3g9IjAgMCA4MSA4MSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgxIiBoZWlnaHQ9IjgxIiBmaWxsPSIjRjBGMEYwIi8+CjwvInZnPgo=';
  const placeholderImage = PLACEHOLDER_IMAGE;
  const { cvAppList } = useSelector((state) => state?.cvAppReducer);
  const currentCvApp = cvAppList?.find(app => app?.id === cvappid);
  const fullDeployParamsSchema = currentCvApp?.configtemplate?.schema?.properties?.[ADVANCED_CONFIG.DEPLOY_PARAMETERS];
  const cvAppName = getFullCVAppName(cvappid, cvAppList);
  // Extract schema properties for the threshold fields from the full deploy-parameters schema
  const enrolledAppearanceParams = fullDeployParamsSchema?.properties?.[ADVANCED_CONFIG.ENROLLED_APPEARANCE_PARAMETERS];
  const captureThresholdSchema = enrolledAppearanceParams?.properties?.[CAPTURE_THRESHOLD_KEY];
  const matchingThresholdSchema = enrolledAppearanceParams?.properties?.[MATCHING_THRESHOLD_KEY];

  //required fields from schema
  const requiredFields = enrolledAppearanceParams?.required || [];
  const isCaptureRequired = requiredFields.includes(CAPTURE_THRESHOLD_KEY);
  const isMatchingRequired = requiredFields.includes(MATCHING_THRESHOLD_KEY);

  // Get state from Redux
  const appearances = useSelector((state) => state.enrolledAppearanceReducer.appearances || []);
  const imageCache = useSelector((state) => state.enrolledAppearanceReducer.imageCache || {});
  const loading = useSelector((state) => state.enrolledAppearanceReducer.loading);
  const siteList = useSelector((state) => state.sitesReducer.siteTagList?.sites || []);
  const cameraSiteName = siteList.find((s) => s.id === siteId)?.name;
  const cameraName = location.state?.cameraname;
  
  const imageLoadingStates = useSelector((state) => state.enrolledAppearanceReducer.imageLoadingStates || {});
  const advancedConfigAppearances = useSelector((state) => state.enrolledAppearanceReducer.advancedConfigAppearances || []);
  const advancedConfigThresholds = useSelector((state) => state.enrolledAppearanceReducer.advancedConfigThresholds || {
    [CAPTURE_THRESHOLD_KEY]: undefined,
    [MATCHING_THRESHOLD_KEY]: undefined
  });
  const advancedConfigRadioSelection = useSelector((state) => state.enrolledAppearanceReducer.advancedConfigRadioSelection);

  const [selectedRadio, setSelectedRadio] = useState(advancedConfigRadioSelection);
  const [appearanceDropdowns, setAppearanceDropdowns] = useState([{ id: 1, value: "", images: [], loading: false }]);
  // Use nullish coalescing (??) to preserve valid 0 values from Redux
  const [captureThreshold, setCaptureThreshold] = useState(
    advancedConfigThresholds?.[CAPTURE_THRESHOLD_KEY] ?? captureThresholdSchema?.default ?? ""
  );
  const [matchingThreshold, setMatchingThreshold] = useState(
    advancedConfigThresholds?.[MATCHING_THRESHOLD_KEY] ?? matchingThresholdSchema?.default ?? ""
  );
  const [isCaptureThresholdError, setIsCaptureThresholdError] = useState(false);
  const [isMatchingThresholdError, setIsMatchingThresholdError] = useState(false);

  // Cross-site enrollment state
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [pendingDropdownId, setPendingDropdownId] = useState(null);
  const [showDeploymentBlockModal, setShowDeploymentBlockModal] = useState(false);
  const [deployError, setDeployError] = useState(null);
  const enrollTargetRef = useRef({ appearanceName: "", appearanceId: "", enrolledSiteIds: [], appearanceStatus: "", deployments: [] });

  const [showProcessingRequiredModal, setShowProcessingRequiredModal] = useState(false);
  const [processingTargetDropdownId, setProcessingTargetDropdownId] = useState(null);
  const [processingTargetAppearanceId, setProcessingTargetAppearanceId] = useState(null);

  useEffect(() => {
    if (!deployError) return;
    const timer = setTimeout(() => setDeployError(null), 5000);
    return () => clearTimeout(timer);
  }, [deployError]);

  // Track which appearances have had one retry attempt (to prevent infinite retry loops)
  const retriedAppearancesRef = useRef({});
  
  // Track if we've already initialized from Redux (to prevent re-restoration on every Redux update)
  const hasInitializedRef = useRef(false);
  
  // Calculate if at least one valid appearance is selected (to reduce license count)
  const hasAnyAppearanceSelected = appearanceDropdowns.some(
    dropdown => dropdown?.value && dropdown.value !== ""
  );

  // Fetch appearances on component mount only if not already loaded
  useEffect(() => {
    if (!appearances || appearances?.length === 0){
      dispatch(fetchAppearancesRequest());
    }
  }, []);

  // Refresh appearances once when returning from create/edit appearance, so newly
  // created/edited appearances show up without relying on the empty-list check above.
  const hasFetchedOnReopenRef = useRef(false);
  useEffect(() => {
    if (location.state?.reopenAdvancedConfig && !hasFetchedOnReopenRef.current) {
      hasFetchedOnReopenRef.current = true;
      dispatch(fetchAppearancesRequest());
    }
  }, []);

  // Initialize Redux with schema defaults when Redux is empty (create flow or edit flow after clearing)
  useEffect(() => {
    // Extract Redux values
    const reduxCaptureValue = advancedConfigThresholds?.[CAPTURE_THRESHOLD_KEY];
    const reduxMatchingValue = advancedConfigThresholds?.[MATCHING_THRESHOLD_KEY];
    const captureDefault = captureThresholdSchema?.default;
    const matchingDefault = matchingThresholdSchema?.default;

    // Helper to check if value is empty (undefined or "")
    const isValueEmpty = (value) => value === undefined || value === "";

    // Only initialize if we have valid schema defaults and Redux is empty
    // This condition naturally prevents infinite loops: after first dispatch,
    // Redux is no longer empty, so this block won't run again
    if (captureDefault !== undefined &&
        matchingDefault !== undefined &&
        isValueEmpty(reduxCaptureValue) &&
        isValueEmpty(reduxMatchingValue)) {
      // Store schema defaults in Redux
      dispatch(setAdvancedConfigThresholds({
        [CAPTURE_THRESHOLD_KEY]: captureDefault,
        [MATCHING_THRESHOLD_KEY]: matchingDefault
      }));
      // set local state
      setCaptureThreshold(captureDefault);
      setMatchingThreshold(matchingDefault);
    }
  }, [captureThresholdSchema?.default, matchingThresholdSchema?.default, dispatch]);

  // Initialize from Redux state when component mounts
  useEffect(() => {
    // Only restore once when component mounts - don't re-run when Redux updates during user interactions
    if (hasInitializedRef.current) {
      return;
    }
    
    // Wait for both advancedConfigAppearances AND appearances to be available before restoring
    if (advancedConfigAppearances && advancedConfigAppearances.length > 0 && appearances && appearances.length > 0) {
      hasInitializedRef.current = true;
      
      // Restore dropdowns from Redux - images will be handled by separate useEffect
      const restoredDropdowns = advancedConfigAppearances.map((item, index) => {
        const appearance = appearances?.find(app => app?.id === item?.value);
        const cachedImages = item?.value ? imageCache[item.value] : null;
        const loadingState = item?.value ? imageLoadingStates[item.value] : null;
        const isCurrentlyLoading = loadingState?.loading || false;
        
        // Get cached images if available
        let images = [];
        if (appearance?.images && appearance.images.length > 0 && cachedImages) {
          images = appearance.images.map(img => {
            const imageId = img?.imageId || img?.id;
            return imageId ? (cachedImages[imageId] || null) : null;
          });
        }
        
        return {
          id: index + 1,
          value: item.value,
          images: images,
          loading: isCurrentlyLoading
        };
      });
      
      setAppearanceDropdowns(restoredDropdowns);
      // Restore threshold values from Redux (moved inside ref guard)
      if (advancedConfigThresholds?.[CAPTURE_THRESHOLD_KEY] !== undefined) {
        setCaptureThreshold(advancedConfigThresholds[CAPTURE_THRESHOLD_KEY]);
      }
      if (advancedConfigThresholds?.[MATCHING_THRESHOLD_KEY] !== undefined) {
        setMatchingThreshold(advancedConfigThresholds[MATCHING_THRESHOLD_KEY]);
      }

      // Restore radio selection from Redux
      setSelectedRadio(advancedConfigRadioSelection);
    }
  }, [advancedConfigAppearances, appearances, imageCache, advancedConfigThresholds]);
  
  // Separate useEffect to sync radio selection without re-restoring dropdowns
  useEffect(() => {
    setSelectedRadio(advancedConfigRadioSelection);
  }, [advancedConfigRadioSelection]);

  // Download missing images for dropdowns
  // Create flow: download once, show image if success, placeholder if error - no retries
  // Edit flow: retry failed images on modal reopen
  useEffect(() => {
    if (!appearanceDropdowns || appearanceDropdowns.length === 0) return;

    appearanceDropdowns.forEach((dropdown) => {
      if (!dropdown.value) return;
      
      // Skip if already attempted for this appearance in last 30 seconds
      const lastRetry = retriedAppearancesRef.current[dropdown.value];
      const now = Date.now();
      if (lastRetry && (now - lastRetry < 30000)) return;      
      const appearance = appearances?.find(app => app?.id === dropdown.value);
      const images = Array.isArray(appearance?.images) ? appearance.images : [];
      if (images.length === 0) return;

      const cacheForAppearance = imageCache[dropdown.value] || {};
      
      // In create flow: only download if completely missing from cache
      // In edit flow: download if missing OR marked as error/failed
      const missingImages = images.filter((img) => {
        const key = img?.imageId;
        if (!key) return true;
        
        const cachedValue = cacheForAppearance[key];
        
        if (action === 'create') {
          // Create flow: only missing images, ignore failed ones
          return !cachedValue;
        } else {
          // Edit flow: missing or failed images
          return !cachedValue || cachedValue === ADVANCED_CONFIG.CACHE_STATUS_ERROR || cachedValue === ADVANCED_CONFIG.CACHE_STATUS_FAILED;
        }
      });

      const { loading } = imageLoadingStates[dropdown.value] || {};
      
      if (missingImages?.length > 0 && !loading) {
        try {
          dispatch(downloadAppearanceImagesRequest(dropdown.value, missingImages));
          retriedAppearancesRef.current[dropdown.value] = Date.now();
        } catch (error) {
          console.error('Failed to dispatch image download request:', error);
       }
      }
    });
  }, [appearanceDropdowns, appearances, imageCache, imageLoadingStates, dispatch, action]);

  // Sync imageCache from Redux to dropdown images
  useEffect(() => {
    setAppearanceDropdowns(prev => 
      prev.map(dropdown => {
        if (!dropdown.value) {
          return dropdown;
        }
        const cachedImages = imageCache[dropdown.value];
        const appearance = appearances.find(app => app.id === dropdown.value);
        const isLoading = imageLoadingStates[dropdown.value];

        // If still loading (according to Redux state), show loading state
        if (isLoading?.loading) {
          return { ...dropdown, loading: true, images: [] };
        }
        
        // Not loading - build mixed array of cached images and nulls
        // This handles: all cached, partially cached, or none cached
        if (!isLoading?.loading && appearance?.images?.length > 0) {
          const imageUrls = appearance.images
            .map(img => {
              const imageId = img?.imageId || img?.id;
              // Try to get cached image, fall back to null
              const cachedUrl = (cachedImages && imageId) ? cachedImages[imageId]?.blobUrl : null;
              // Return cached URL if found, otherwise null
              return cachedUrl;
            });
          
          return { ...dropdown, images: imageUrls, loading: false };
        }
        return dropdown;
      })
    );
  }, [imageCache, imageLoadingStates, appearances]);

  // Validate threshold values - check for empty and range violations
  useEffect(() => {
    const captureEmpty = captureThreshold === "" || captureThreshold === null || captureThreshold === undefined;
    const matchingEmpty = matchingThreshold === "" || matchingThreshold === null || matchingThreshold === undefined;

    // Get min/max from schema
    const captureMin = captureThresholdSchema?.minimum || ADVANCED_CONFIG.MINIMUM_VAL;
    const captureMax = captureThresholdSchema?.maximum || ADVANCED_CONFIG.MAX_VAL;
    const matchingMin = matchingThresholdSchema?.minimum || ADVANCED_CONFIG.MINIMUM_VAL;
    const matchingMax = matchingThresholdSchema?.maximum || ADVANCED_CONFIG.MAX_VAL;

    // Validate capture threshold: error if empty OR out of range
    if (captureEmpty) {
      setIsCaptureThresholdError(true);
    } else {
      const captureValue = Number(captureThreshold);
      const captureInvalid = isNaN(captureValue) || captureValue < captureMin || captureValue > captureMax;
      setIsCaptureThresholdError(captureInvalid);
    }

    // Validate matching threshold: error if empty OR out of range
    if (matchingEmpty) {
      setIsMatchingThresholdError(true);
    } else {
      const matchingValue = Number(matchingThreshold);
      const matchingInvalid = isNaN(matchingValue) || matchingValue < matchingMin || matchingValue > matchingMax;
      setIsMatchingThresholdError(matchingInvalid);
    }
  }, [captureThreshold, matchingThreshold, captureThresholdSchema, matchingThresholdSchema]);

  // Validation: Check if enrolled_appearance is enabled and at least one appearance is selected
  useEffect(() => {
    const hasSelectedAppearance = appearanceDropdowns?.some(dropdown => dropdown.value);
    const hasSelectedWithNoImages = appearanceDropdowns?.some(dropdown => {
      if (!dropdown.value) return false;
      const app = appearances.find((a) => a.id === dropdown.value);
      return !(app?.images?.length);
    });

    // Validation should fail if:
    // 1. No appearance is selected
    // 2. Selected appearance has no images
    // 3. There are errors in threshold fields
    const shouldAddError = !hasSelectedAppearance || hasSelectedWithNoImages || isCaptureThresholdError || isMatchingThresholdError;
    if (shouldAddError) {
      // Add error if validation fails
      setErrorInFormInput((prev) => {
        const prevArray = Array.isArray(prev) ? prev : [];
        if (prevArray.includes(ADVANCED_CONFIG.APPEARANCES)) {
          return prevArray;
        } else {
          const newErrors = [...prevArray, ADVANCED_CONFIG.APPEARANCES];
          return newErrors;
        }
      });
    } else {
      // Clear error if validation passes
      setErrorInFormInput((prev) => {
        const prevArray = Array.isArray(prev) ? prev : [];
        if (prevArray.includes(ADVANCED_CONFIG.APPEARANCES)) {
          const newErrors = prevArray?.filter(item => item !== ADVANCED_CONFIG.APPEARANCES);
          return newErrors;
        } else {
          return prevArray;
        }
      });
    }
  }, [appearanceDropdowns, payload, parameterKeyName, isCaptureThresholdError, isMatchingThresholdError, setErrorInFormInput]);
  // Cleanup: Clear error when component unmounts (but keep Redux state for reuse)
  useEffect(() => {
    return () => {
      // Clear error when component unmounts
      setErrorInFormInput((prev) => {
        if (Array.isArray(prev) && prev.includes(ADVANCED_CONFIG.APPEARANCES)) {
          return prev.filter(item => item !== ADVANCED_CONFIG.APPEARANCES);
        } else {
          return prev;
        }
      });
      
      // Reset initialization and retry flags so restoration works fresh on next mount
      hasInitializedRef.current = false;
      retriedAppearancesRef.current = {};
      
    };
  }, [dispatch]);

  const handleRadioChange = (e) => {
    const value = e.target.value;
    setSelectedRadio(value);
    dispatch(setAdvancedConfigRadio(value));
    setIsTampered(true);
    // Immediately update payload with the new radio value
updatePayloadWithAppearances(appearanceDropdowns, captureThreshold, matchingThreshold, value);  };

  // Helper function to update payload and Redux with selected appearances
  const updatePayloadWithAppearances = (dropdowns, threshold1 = captureThreshold, threshold2 = matchingThreshold, radioValue = selectedRadio) => {
    // Build appearance array with appearance_id and appearance_name (optimized single pass)
    const appearanceArray = [];
    const appearancesToSave = [];
    
    dropdowns.forEach(d => {
      if (d.value) {
        const appearance = appearances.find(app => app.id === d.value);
        if (appearance) {
          appearanceArray.push({
            [ADVANCED_CONFIG.APPEARANCE_ID]: d.value,
            [ADVANCED_CONFIG.APPEARANCE_NAME]: appearance.name || '',
          });
          appearancesToSave.push({
            value: d.value,
            name: appearance.name || ''
          });
        }
      }
    });

    // Save appearances to Redux
    dispatch(setAdvancedConfigAppearances(appearancesToSave));
    
    // Save thresholds to Redux
    dispatch(setAdvancedConfigThresholds({
      [CAPTURE_THRESHOLD_KEY]: threshold1,
      [MATCHING_THRESHOLD_KEY]: threshold2
    }));

    // Build the new parameters object
    const newParams = {
      ...payload.config[parameterKeyName]
    };

    // Remove old fields if they exist (for backward compatibility)
    delete newParams.in_watchlist;
    delete newParams.watchlist;
    delete newParams[CAPTURE_THRESHOLD_KEY];
    delete newParams[MATCHING_THRESHOLD_KEY];

    // Create the enrolled_appearance_parameters object with new structure
    // If value is empty string, keep it empty (for validation to catch)
    // Otherwise use value, or schema default if value is null/undefined
    const threshold1Value = threshold1 === "" ? "" : (threshold1 ?? captureThresholdSchema?.default);
    const threshold2Value = threshold2 === "" ? "" : (threshold2 ?? matchingThresholdSchema?.default);

    newParams[keyName] = {
      [ADVANCED_CONFIG.IN_APPEARANCE]: radioValue === "option2", // option2 = "IS" in appearance
      appearance: appearanceArray, // Array of {appearance_id, appearance_name}
      [CAPTURE_THRESHOLD_KEY]: threshold1Value,
      [MATCHING_THRESHOLD_KEY]: threshold2Value
    };

    // Save to payload for API with nested structure
    try{
    setPayload({
      ...payload,
      config: {
        ...payload.config,
        [parameterKeyName]: newParams
      },
    });
    setIsTampered(true);
    } catch (error) {
      console.error('Failed to update payload:', error);
    }

  };

  const handleClearSelection = (id) => {
    setAppearanceDropdowns(prev =>
      prev.map(dropdown =>
        dropdown.id === id 
          ? { ...dropdown, value: "", images: [], loading: false } 
          : dropdown
      )
    );
    // Update payload to remove this appearance
    updatePayloadWithAppearances(
      appearanceDropdowns.map(d => d.id === id ? { ...d, value: "" } : d)
    );
  };



  const handleExistingAppearanceSelection = (id, value) => {
    // Find the selected appearance from API data
    const selectedAppearance = appearances.find(app => app?.id === value);
    
    if (!selectedAppearance?.images?.length) {
      // Clear images if no images available and update immediately
      setAppearanceDropdowns(prev =>
        prev.map(dropdown =>
          dropdown.id === id 
            ? { ...dropdown, value, images: [], loading: false } 
            : dropdown
        )
      );
      updatePayloadWithAppearances(
        appearanceDropdowns.map(d => d.id === id ? { ...d, value, images: [] } : d)
      );
      return;
    }
    
    // Check if images are already cached
    const cachedImages = imageCache[value];
    const allImagesCached = cachedImages && selectedAppearance.images.every(img => {
      const imageId = img?.imageId || img?.id;
      return imageId && cachedImages[imageId];
    });
    
    if (allImagesCached) {
      // All images are already cached, use them immediately without loading state
      const imageUrls = selectedAppearance.images
        .map(img => {
          const imageId = img?.imageId || img?.id;
          return imageId ? cachedImages[imageId]?.blobUrl : null;
        })
        .filter(url => url);
      
      setAppearanceDropdowns(prev =>
        prev.map(dropdown =>
          dropdown.id === id 
            ? { ...dropdown, value, images: imageUrls, loading: false } 
            : dropdown
        )
      );
      updatePayloadWithAppearances(
        appearanceDropdowns.map(d => d.id === id ? { ...d, value } : d)
      );
      return;
    }
    
    // Images not fully cached - set loading state and dispatch download request
    setAppearanceDropdowns(prev =>
      prev.map(dropdown =>
        dropdown.id === id 
          ? { ...dropdown, value, loading: true, images: [] } 
            : dropdown
      )
    );
    
    // Dispatch download API to get missing images
    dispatch(
      downloadAppearanceImagesRequest(
        value,
        selectedAppearance.images
      )
    );
    
    // Update local state with value - images will be updated from Redux via useEffect when download completes
    updatePayloadWithAppearances(
      appearanceDropdowns.map(d => d.id === id ? { ...d, value } : d)
    );
  };

  const handleEnrollSuccess = () => {
    setShowDeployModal(false);
    const { appearanceId } = enrollTargetRef.current;
    if (pendingDropdownId && appearanceId) {
      handleExistingAppearanceSelection(pendingDropdownId, appearanceId);
    }
    setPendingDropdownId(null);
  };

  const handleDeployFailure = (errorMsg) => {
    setDeployError(errorMsg || APPEARANCE_TEXT.DEPLOY_FAILED_FALLBACK);
  };

  const handleDropdownChange = (id, value) => {
    if (!value) {
      return handleClearSelection(id);
    }

    const selectedAppearance = appearances.find((app) => app?.id === value);
    if (selectedAppearance && siteId && !(selectedAppearance.enrolledSiteIds || []).includes(siteId)) {
      setPendingDropdownId(id);
      enrollTargetRef.current = {
        appearanceName: selectedAppearance.name || "",
        appearanceId: selectedAppearance.id,
        enrolledSiteIds: selectedAppearance.enrolledSiteIds || [],
        appearanceStatus: selectedAppearance.status || "",
        deployments: selectedAppearance.deployments || []
      };
      // Set value optimistically so dropdown doesn't show empty/required validation
      setAppearanceDropdowns((prev) =>
        prev.map((dropdown) =>
          dropdown.id === id ? { ...dropdown, value, images: [], loading: false } : dropdown
        )
      );
      setDeployError(null);
      setShowDeployModal(true);
      return;
    }

    // Processing required: non-cross-site, appearance state must be VALID and submitted before use
    if (selectedAppearance?.status === APPEARANCE_RUNTIME_STATUS.VALID) {
      setProcessingTargetDropdownId(id);
      setProcessingTargetAppearanceId(value);
      setAppearanceDropdowns((prev) =>
        prev.map((dropdown) =>
          dropdown.id === id ? { ...dropdown, value, images: [], loading: false } : dropdown
        )
      );
      setShowProcessingRequiredModal(true);
      return;
    }

    handleExistingAppearanceSelection(id, value);
  };

  const addAppearanceDropdown = () => {
    const newId = Math.max(...appearanceDropdowns.map(d => d.id), 0) + 1;
    setAppearanceDropdowns([...appearanceDropdowns, { id: newId, value: "", images: [], loading: false }]);
    setIsTampered(true);
  };

  const removeAppearanceDropdown = (id) => {
    let updatedDropdowns;
    if (appearanceDropdowns.length > 1) {
      updatedDropdowns = appearanceDropdowns.filter(dropdown => dropdown.id !== id);
      setAppearanceDropdowns(updatedDropdowns);
    } else {
      // If it's the last one, reset it instead of removing
      updatedDropdowns = [{ id: 1, value: "", images: [], loading: false }];
      setAppearanceDropdowns(updatedDropdowns);
    }
    // Update payload after removing (this calls setIsTampered)
    updatePayloadWithAppearances(updatedDropdowns);
  };

  const handleResetToDefault = () => {
    const captureDefault = captureThresholdSchema?.default;
    const matchingDefault = matchingThresholdSchema?.default;
    setCaptureThreshold(captureDefault);
    setMatchingThreshold(matchingDefault);
    dispatch(setAdvancedConfigThresholds({
      [CAPTURE_THRESHOLD_KEY]: captureDefault,
      [MATCHING_THRESHOLD_KEY]: matchingDefault
    }));
    
    // Update payload with default threshold values
    if (payload?.config?.[parameterKeyName]?.[ADVANCED_CONFIG.ENROLLED_APPEARANCE_PARAMETERS]) {
      setPayload({
        ...payload,
        config: {
          ...payload.config,
          [parameterKeyName]: {
            ...payload.config[parameterKeyName],
            [ADVANCED_CONFIG.ENROLLED_APPEARANCE_PARAMETERS]: {
              ...payload.config[parameterKeyName][ADVANCED_CONFIG.ENROLLED_APPEARANCE_PARAMETERS],
              [CAPTURE_THRESHOLD_KEY]: captureDefault,
              [MATCHING_THRESHOLD_KEY]: matchingDefault
            }
          }
        }
      });
    }
    
    setIsTampered(true);
    // Clear threshold errors since we're resetting to valid schema defaults
    setIsCaptureThresholdError(captureDefault ? false : true);
    setIsMatchingThresholdError(matchingDefault ? false : true);
    //Clear form-level errors if both defaults are valid
    setErrorInFormInput((prev) => {
      const prevArray = Array.isArray(prev) ? prev : [];
      if (captureDefault && matchingDefault && hasAnyAppearanceSelected) {
        // Remove error if both defaults exist
        return prevArray.filter(item => item !== ADVANCED_CONFIG.APPEARANCES);
      } else {
        // Add error if either default is missing
        if (!prevArray.includes(ADVANCED_CONFIG.APPEARANCES)) {
          return [...prevArray, ADVANCED_CONFIG.APPEARANCES];
        }
        return prevArray;
      }
    });
  };

  const renderAppearanceOptions = (dropdown) => {
    return appearances
      .filter(appearance => {
        // Filter out appearances that are already selected in other dropdowns
        const selectedValues = appearanceDropdowns
          .filter(d => d?.id !== dropdown?.id) // Exclude current dropdown
          .map(d => d?.value);
        return appearance?.id && !selectedValues.includes(appearance.id);
      })
      .map((appearance) => (
        <option key={appearance?.id} value={appearance?.id}>
          {appearance?.name || 'Unknown'}
        </option>
      ));
  };

  const handleAddAppearanceClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const lastDropdown = appearanceDropdowns[appearanceDropdowns.length - 1];
    const hasValidAppearance = lastDropdown?.value;
    
    // Only add if last dropdown has a valid selection
    if (hasValidAppearance) {
      addAppearanceDropdown();
    }
  };



  const handleProcessingModalClose = () => {
    setShowProcessingRequiredModal(false);
    setAppearanceDropdowns((prev) =>
      prev.map((dropdown) =>
        dropdown.id === processingTargetDropdownId ? { ...dropdown, value: "", images: [], loading: false } : dropdown
      )
    );
    setProcessingTargetDropdownId(null);
    setProcessingTargetAppearanceId(null);
  };

  const handleProcessingModalSuccess = () => {
    const dropdownId = processingTargetDropdownId;
    const appearanceId = processingTargetAppearanceId;
    setShowProcessingRequiredModal(false);
    setProcessingTargetDropdownId(null);
    setProcessingTargetAppearanceId(null);
    handleExistingAppearanceSelection(dropdownId, appearanceId);
  };

  // EACreate has no access to the Polygon page's breadcrumb context (camera, cvapp, roi data lives
  // in location.state only on the Polygon page). EnhancedToggleField renders ON the Polygon page,
  // so location.state here IS the Polygon state. We mirror Polygon's breadcrumb logic here and pass
  // the result to EACreate so it can render the full chain without prop drilling.
  const buildBreadcrumbItems = () => {
    const polygonState = location.state;
    const polygonLocation = polygonState?.location;
    const returnNavState = { ...polygonState, reopenAdvancedConfig: true, savedPayload: payload };
    const roiPolygonItem = {
      navigateTo: location.pathname + location.search,
      navigateState: returnNavState,
      breadcrumbText: APPEARANCE_TEXT.ROI_POLYGON_CONFIGURATION
    };

    if (polygonLocation === ASSOCIATE_STREAMS) {
      return [
        { navigateTo: ROUTES.ENROLLED_APPEARANCE, breadcrumbText: APPEARANCE_TEXT.ENROLLED_APPEARANCES_BREADCRUMB },
        { navigateTo: ROUTES.APPEARANCE_DEPLOYMENTS, navigateState: { appearance: polygonState?.appearance }, breadcrumbText: polygonState?.appearanceName || APPEARANCE_TEXT.APPEARANCE_NAME_FALLBACK },
        roiPolygonItem
      ];
    }

    if (polygonLocation === "roilist") {
      const cameraDataItem = polygonState?.cameraData;
      const items = [{ navigateTo: ROUTES.DEVICES, navigateState: { tabName: "Camera" }, breadcrumbText: APPEARANCE_TEXT.CAMERAS_BREADCRUMB }];
      if (polygonState?.originPage === "cameraDetails" && cameraDataItem?.name) {
        items.push({
          navigateTo: `${ROUTES.CAMERA_INFO_PAGE}?cameraId=${cameraDataItem?.id}`,
          navigateState: { data: cameraDataItem },
          breadcrumbText: cameraDataItem.name
        });
      }
      items.push({
        navigateTo: `${ROUTES.ROI}?cameraId=${cameraDataItem?.id}&fovId=${polygonState?.FOVId}`,
        navigateState: {
          data: cameraDataItem,
          streamData: polygonState?.streamData,
          FOVId: polygonState?.FOVId,
          FOVSnapshot: polygonState?.FOVSnapshot,
          fieldOfViews: polygonState?.fieldOfViews,
          FOVName: polygonState?.FOVName,
          originPage: polygonState?.originPage || ""
        },
        breadcrumbText: APPEARANCE_TEXT.REGIONS_OF_INTEREST_BREADCRUMB
      });
      items.push(roiPolygonItem);
      return items;
    }

    if (polygonLocation === "eventsInfo") {
      const items = [{ navigateTo: ROUTES.DEVICES, navigateState: { tabName: "Camera" }, breadcrumbText: APPEARANCE_TEXT.CAMERAS_BREADCRUMB }];
      if (polygonState?.originPage === "eventsInfo") {
        items.push({
          navigateTo: `${ROUTES.EVENTS_INFO}?eventid=${polygonState?.eventId}`,
          breadcrumbText: APPEARANCE_TEXT.EVENT_DETAILS_BREADCRUMB
        });
      }
      items.push(roiPolygonItem);
      return items;
    }

    // Default: CVAPP flow
    const cameraDataItem = polygonState?.cameradata?.find(item => item?.id === polygonState?.cameraId);
    const items = [{ navigateTo: ROUTES.DEVICES, navigateState: { tabName: "Camera" }, breadcrumbText: APPEARANCE_TEXT.CAMERAS_BREADCRUMB }];
    if (polygonState?.originPage === "cameraDetails" && cameraDataItem?.name) {
      items.push({
        navigateTo: `${ROUTES.CAMERA_INFO_PAGE}?cameraId=${polygonState?.cameraId}`,
        navigateState: { data: cameraDataItem },
        breadcrumbText: cameraDataItem.name
      });
    }
    items.push({
      navigateTo: ROUTES.CVAPP_SUB,
      navigateState: {
        cameraId: polygonState?.cameraId,
        companionId: polygonState?.companionId,
        FOVId: polygonState?.FOVId,
        FOVImages: polygonState?.FOVSnapshot,
        fieldOfViews: polygonState?.fieldOfViews,
        FOVNames: polygonState?.FOVNames,
        cameraname: polygonState?.cameraname,
        action: polygonState?.fromAction,
        cameradata: polygonState?.cameradata,
        sku: polygonState?.sku,
        originPage: polygonState?.originPage || "",
        roiList: polygonState?.roiList
      },
      breadcrumbText: APPEARANCE_TEXT.CV_APP_CONFIGURATION_BREADCRUMB
    });
    items.push(roiPolygonItem);
    return items;
  };

  // Reset dropdowns, thresholds and radio selection (Redux + local state) before
  // navigating away, so returning to Advanced Config never shows stale selections.
  const resetAdvancedConfigSelections = (callFrom) => {
    dispatch(clearAdvancedConfigAppearances(callFrom));
    setAppearanceDropdowns([{ id: 1, value: "", images: [], loading: false }]);
    setCaptureThreshold("");
    setMatchingThreshold("");
    setSelectedRadio("option1");
    setIsCaptureThresholdError(false);
    setIsMatchingThresholdError(false);
  };

  const buildNavigateAwayState = () => ({
    fromAdvancedConfig: true,
    returnPath: location.pathname + location.search,
    polygonState: location.state,
    breadcrumbItems: buildBreadcrumbItems(),
    savedPayload: payload
  });

  const handleNavigateToCreateAppearance = () => {
    resetAdvancedConfigSelections(ADVANCED_CONFIG.NAVIGATE_AWAY_CREATE_APPEARANCE);
    navigate(ROUTES.CREATE_APPEARANCE, { state: buildNavigateAwayState() });
  };

  const handleNavigateToEditAppearance = (appearanceId) => {
    resetAdvancedConfigSelections(ADVANCED_CONFIG.NAVIGATE_AWAY_EDIT_APPEARANCE);
    navigate(`${ROUTES.EDIT_APPEARANCE}?id=${appearanceId}`, { state: buildNavigateAwayState() });
  };

  // This component is only rendered when enrolled_appearance is enabled
  // So we don't need a toggle here - just render the content directly
  return (
    <>
      {showDeployModal && (
        <DeployModal
          appearanceName={enrollTargetRef.current.appearanceName}
          appearanceId={enrollTargetRef.current.appearanceId}
          tagids={enrollTargetRef.current.enrolledSiteIds}
          appearanceStatus={enrollTargetRef.current.appearanceStatus}
          deployments={enrollTargetRef.current.deployments}
          opened={showDeployModal}
          closeModal={() => {
            setShowDeployModal(false);
            if (pendingDropdownId !== null) {
              setAppearanceDropdowns((prev) =>
                prev.map((dropdown) =>
                  dropdown.id === pendingDropdownId ? { ...dropdown, value: "", images: [], loading: false } : dropdown
                )
              );
            }
            setPendingDropdownId(null);
          }}
          enrollOnly={enrollTargetRef.current.appearanceStatus !== APPEARANCE_RUNTIME_STATUS.VALID}
          onDeploySuccess={handleEnrollSuccess}
          onDeployBlockAttempt={() => setShowDeploymentBlockModal(true)}
          onDeployFailure={handleDeployFailure}
          infoMessage={buildEnrollInfoMessage(cameraSiteName, cameraName)}
          requiredSiteId={siteId}
        />
      )}
      <ProcessingRequiredModal
        opened={showProcessingRequiredModal}
        appearanceId={processingTargetAppearanceId}
        onClose={handleProcessingModalClose}
        onSuccess={handleProcessingModalSuccess}
      />
      <ErrorModal
        opened={showDeploymentBlockModal}
        title={`${APPEARANCE_TEXT.DEPLOYMENT_UNENROLL_LOCK_TITLE} ${enrollTargetRef.current.appearanceName}`}
        body={
          <span>
            {APPEARANCE_TEXT.DEPLOYMENT_LOCK_BODY_BEFORE}
            <span style={{ textDecoration: "underline" }}>{APPEARANCE_TEXT.DEPLOYMENT_LOCK_LINK_TEXT}</span>
            {APPEARANCE_TEXT.DEPLOYMENT_LOCK_BODY_AFTER}
          </span>
        }
        footerButtonText={CREATE_APPEARANCE.CLOSE}
        onClose={() => setShowDeploymentBlockModal(false)}
        ariaLabel="Deployment lock modal"
      />
      <Loader active={loading} fullscreen={false} />
      <Line type="secondary" style={{ color: "grey" }} />
      {deployError && (
        <div style={{ marginBottom: 12 }}>
          <Notification type="error" title={APPEARANCE_TEXT.DEPLOY_TO_COMPANION_ERROR} subtitle={deployError} disableFocus={false} />
        </div>
      )}
      <div className="content-wrapper" style={style}>
        {/* Radio Buttons Section */}
        <FormControl>
          <RadioGroup
            value={selectedRadio}
            onChange={handleRadioChange}
            sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginBottom: '8px'
            }}
          >
            <FormControlLabel
              value="option1"
              control={<Radio sx={{ "&.Mui-checked": { color: "black" } }} />}
              label={
                <>
                  <Body color="#000000" size="large">
                    {cvAppName === CVAPP_PEOPLE_COUNT_OA ? ADVANCED_CONFIG.PC_RADIO_OPTION_PREFIX : ADVANCED_CONFIG.RADIO_OPTION_TITLE} <strong>{ADVANCED_CONFIG.RADIO_OPTION1_BOLD}</strong> {ADVANCED_CONFIG.RADIO_OPTION1_SUFFIX}
                  </Body>
                  <div>
                    <Body size="small" color="#6F7171">
                      {cvAppName === CVAPP_PEOPLE_COUNT_OA ? ADVANCED_CONFIG.PC_RADIO_OPTION1_DESCRIPTION : ADVANCED_CONFIG.RADIO_OPTION1_DESCRIPTION}
                    </Body>
                  </div>
                </>
              }
            />
            <FormControlLabel
              value="option2"
              control={<Radio sx={{ "&.Mui-checked": { color: "black" } }} />}
              label={
                <>
                  <Body color="#000000" size="large">
                    {cvAppName === CVAPP_PEOPLE_COUNT_OA ? ADVANCED_CONFIG.PC_RADIO_OPTION_PREFIX : ADVANCED_CONFIG.RADIO_OPTION_TITLE} <strong>{ADVANCED_CONFIG.RADIO_OPTION2_BOLD}</strong> {ADVANCED_CONFIG.RADIO_OPTION2_SUFFIX}
                  </Body>
                  <Body size="small" color="#6F7171">
                    {cvAppName === CVAPP_PEOPLE_COUNT_OA ? ADVANCED_CONFIG.PC_RADIO_OPTION2_DESCRIPTION : ADVANCED_CONFIG.RADIO_OPTION2_DESCRIPTION}
                  </Body>
                </>
              }
            />
          </RadioGroup>
        </FormControl>

        {/* Appearance Dropdowns with Images */}
        {appearances.length === 0 ? (
          <NoAppearancesMessage>
            <Body size="medium" color="#6F7171">
              {ADVANCED_CONFIG.NO_ENROLLED_APPEARANCES_PREFIX}
              <span className="ea-text-link" onClick={handleNavigateToCreateAppearance}>
                {ADVANCED_CONFIG.NO_ENROLLED_APPEARANCES_LINK}
              </span>
              {ADVANCED_CONFIG.NO_ENROLLED_APPEARANCES_SUFFIX}
            </Body>
          </NoAppearancesMessage>
        ) : (
          <div className="appearance-dropdowns-wrapper">
          {appearanceDropdowns.map((dropdown, index) => (
              <div className="dropdown-item-wrapper" key={dropdown.id}>
                {/* Dropdown */}
                <div className="dropdown-row">
                  <div className="dropdown-container">
                    <DropdownSelect
                      label={ADVANCED_CONFIG.APPEARANCE_LABEL}
                      value={dropdown.value}
                      onChange={(e) => handleDropdownChange(dropdown.id, e.target.value)}
                      disabled={false}
                      width="606px"
                    >
                      <option value="">{ADVANCED_CONFIG.SELECT_DROPDOWN}</option>
                      {renderAppearanceOptions(dropdown)}
                    </DropdownSelect>
                  </div>
                  {/* Delete Icon - only show if an appearance is selected */}
                  {dropdown?.value && dropdown.value !== "" && (
                    <div
                      className="delete-icon-wrapper"
                      onClick={() => removeAppearanceDropdown(dropdown.id)}
                    >
                      <Icon name="trash" size="large" color="#000000" />
                    </div>
                  )}
                </div>

                {/* Image Gallery for this dropdown */}
                {dropdown?.loading ? (
                  <div className="loading-wrapper">
                    <Body size="medium" color="#6F7171">{ADVANCED_CONFIG.LOADING_IMAGES}</Body>
                  </div>
                ) : dropdown?.value && appearances.find(app => app.id === dropdown.value)?.images?.length > 0 ? (
                  <ImageGalleryContainer>
                    {appearances.find(app => app.id === dropdown.value).images.map((image, index) => {
                        const cachedImages = imageCache[dropdown.value] || {};
                        const imageKey = image.imageId;
                        const imageUrl = (imageKey ? cachedImages[imageKey]?.blobUrl : undefined) || image.url || placeholderImage;
                        return (
                          <ThumbnailImage
                            key={image.id || index}
                            src={imageUrl}
                            alt={`Thumbnail ${index + 1}`}
                            onError={(e) => {
                              if (e.target.src !== placeholderImage) {
                                e.target.src = placeholderImage;
                              }
                            }}
                          />
                        );
                      })}
                  </ImageGalleryContainer>
                ) : dropdown?.value && !dropdown?.loading ? (
                  <div className="loading-wrapper">
                    <Body size="medium" color="#6F7171">
                      {ADVANCED_CONFIG.NO_IMAGES_AVAILABLE_PREFIX}
                      <span
                        className="ea-text-link"
                        onClick={() => handleNavigateToEditAppearance(dropdown.value)}
                      >
                        {ADVANCED_CONFIG.NO_IMAGES_AVAILABLE_LINK}
                      </span>
                      {ADVANCED_CONFIG.NO_IMAGES_AVAILABLE_SUFFIX}
                    </Body>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}

        {/* Add Appearance Button - Only enabled if last dropdown has a selection */}
        <div
          className={`add-appearance-button ${(() => {
            const lastDropdown = appearanceDropdowns[appearanceDropdowns.length - 1];
            const hasValidAppearance = lastDropdown?.value;
            const disableAddApp = appearanceDropdowns?.length === appearances?.length;
              return !hasValidAppearance || disableAddApp ? 'disabled' : '';
          })()}`}
          onClick={handleAddAppearanceClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === ADVANCED_CONFIG.KEY_ENTER || e.key === ADVANCED_CONFIG.KEY_SPACE) {
              handleAddAppearanceClick(e);
            }
          }}
        >
          <Icon name="plus" color="#000" size="large" />
          <Body
size="large"
bold
viewport="desktop"
primitive="span"
color="#000000"
>
            {ADVANCED_CONFIG.ADD_APPEARANCE}
          </Body>
        </div>

        {/* Number Input Fields */}
        <div className="threshold-inputs-wrapper">
          <div className="threshold-input-container">
            <NumberInput
              label={captureThresholdSchema?.title || ADVANCED_CONFIG.CAPTURE_THRESHOLD_LABEL}
              value={captureThreshold}
              onChange={(newValue) => {
                setCaptureThreshold(newValue);
                setIsTampered(true);
                // Update payload with new threshold value
                updatePayloadWithAppearances(appearanceDropdowns, newValue, matchingThreshold);
              }}
              error={isCaptureThresholdError}
              isError={isCaptureThresholdError}
              setIsError={setIsCaptureThresholdError}
              min={captureThresholdSchema?.minimum || 1}
              max={captureThresholdSchema?.maximum || 99}
              helperText={`${ADVANCED_CONFIG.DEFAULT_LABEL} ${captureThresholdSchema?.default || ADVANCED_CONFIG.NA}`}
              helperTextPlacement="bottom"
              errorText={`${ADVANCED_CONFIG.ERROR_RANGE} ${captureThresholdSchema?.minimum || 1}-${captureThresholdSchema?.maximum || 99}`}
              disallowZero={true}
              required={isCaptureRequired}
              children={captureThresholdSchema?.description}
            />
          </div>
          <div className="threshold-input-container">
            <NumberInput
              label={matchingThresholdSchema?.title || ADVANCED_CONFIG.MATCHING_THRESHOLD_LABEL}
              value={matchingThreshold}
              onChange={(newValue) => {
                setMatchingThreshold(newValue);
                setIsTampered(true);
                // Update payload with new threshold value
                updatePayloadWithAppearances(appearanceDropdowns, captureThreshold, newValue);
              }}
              error={isMatchingThresholdError}
              isError={isMatchingThresholdError}
              setIsError={setIsMatchingThresholdError}
              min={matchingThresholdSchema?.minimum || 1}
              max={matchingThresholdSchema?.maximum || 99}
              helperText={`${ADVANCED_CONFIG.DEFAULT_LABEL} ${matchingThresholdSchema?.default || ADVANCED_CONFIG.NA}`}
              helperTextPlacement="bottom"
              errorText={`${ADVANCED_CONFIG.ERROR_RANGE} ${matchingThresholdSchema?.minimum || 1}-${matchingThresholdSchema?.maximum || 99}`}
              disallowZero={true}
              required={isMatchingRequired}
              children={matchingThresholdSchema?.description}
            />
          </div>
        </div>

        {/* Reset Button */}
        <div>
          <Button
          use="secondary"
          role="button"
          size="small"
          surface="lightPrimary"
          onClick={handleResetToDefault}
          width="auto"
        >
            {ADVANCED_CONFIG.RESET_TO_DEFAULT}
          </Button>
        </div>
      </div>
    </>
  );
};